/**
 * IONLOOP Assistant — entity memory graph.
 *
 * Builds a lightweight relationship graph between product entities derived
 * from the indexed pages: category membership, inter-page links and shared
 * topics. Used to generate related-topic suggestions and to continue a topic
 * across turns.
 */

var tokenizer = require('../knowledge/tokenizer');

/**
 * Build a relationship graph from the knowledge documents.
 */
function buildGraph(documents) {
    var docs = documents || [];
    var nodes = {};          // entity key → { name, category, url, docId }
    var related = {};        // entity key → { [otherKey]: weight }
    var categoryMembers = {}; // category → [entityKeys]

    function addEntity(name, category, url, docId) {
        if (!name) { return; }
        var key = tokenizer.normalizePhrase(name);
        if (!key || key.length < 2) { return; }
        if (!nodes[key]) {
            nodes[key] = { name: name, category: category || '', url: url || '', docId: docId || '' };
            related[key] = {};
        }
    }

    function link(aKey, bKey, weight) {
        if (!aKey || !bKey || aKey === bKey) { return; }
        related[aKey][bKey] = (related[aKey][bKey] || 0) + weight;
        related[bKey][aKey] = (related[bKey][aKey] || 0) + weight;
    }

    docs.forEach(function (doc) {
        addEntity(doc.title, doc.category, doc.url, doc.id);
        var docKey = tokenizer.normalizePhrase(doc.title);
        if (!categoryMembers[doc.category]) { categoryMembers[doc.category] = []; }
        if (docKey && categoryMembers[doc.category].indexOf(docKey) === -1) {
            categoryMembers[doc.category].push(docKey);
        }

        (doc.sections || []).forEach(function (section) {
            // Section headings are child concepts of the document.
            addEntity(section.heading, doc.category, doc.url, doc.id);
            var sectionKey = tokenizer.normalizePhrase(section.heading);
            link(docKey, sectionKey, 1);

            // Cross-page links create edges to other entities.
            (section.links || []).forEach(function (linkInfo) {
                var url = linkInfo.url || '';
                var target = docs.find(function (d) { return d.url === url; });
                if (target && target.title) {
                    var targetKey = tokenizer.normalizePhrase(target.title);
                    addEntity(target.title, target.category, target.url, target.id);
                    link(docKey, targetKey, 1);
                } else {
                    addEntity(linkInfo.label, doc.category, url, doc.id);
                    link(docKey, tokenizer.normalizePhrase(linkInfo.label), 0.5);
                }
            });
        });
    });

    return {
        nodes: nodes,
        related: related,
        categoryMembers: categoryMembers,
        getNode: function (name) {
            return nodes[tokenizer.normalizePhrase(name)] || null;
        },
        /**
         * Return related entity names for a given entity/topic.
         */
        getRelated: function (name, limit) {
            limit = limit || 4;
            var key = tokenizer.normalizePhrase(name);
            var neighbours = related[key] || {};
            return Object.keys(neighbours)
                .sort(function (a, b) { return neighbours[b] - neighbours[a]; })
                .slice(0, limit)
                .map(function (k) { return nodes[k] ? nodes[k].name : k; });
        },
        /**
         * Return sibling entities within the same category.
         */
        getCategoryMembers: function (category, limit) {
            limit = limit || 5;
            var keys = categoryMembers[category] || [];
            return keys.slice(0, limit).map(function (k) { return nodes[k] ? nodes[k].name : k; });
        },
        /**
         * Find an entity whose name contains or matches the given term.
         */
        findEntity: function (term) {
            var key = tokenizer.normalizePhrase(term);
            if (nodes[key]) { return nodes[key]; }
            var candidates = Object.keys(nodes).filter(function (k) {
                return k.indexOf(key) !== -1 || key.indexOf(k) !== -1;
            });
            if (candidates.length) {
                candidates.sort(function (a, b) { return Math.abs(a.length - key.length) - Math.abs(b.length - key.length); });
                return nodes[candidates[0]];
            }
            return null;
        }
    };
}

module.exports = {
    buildGraph: buildGraph
};
